/**
 * The AI arm of onboarding: «Что вы поставляете?» → «Проверьте, что получилось».
 *
 * It replaces ONE step of the wizard — the category picker — and nothing else.
 * Everything around it (the step counter, the Telegram step, the ИНН step, the
 * gate that decides whether the modal opens at all) is untouched, and
 * `onManual` hands control straight back to the picker at any point. That is
 * the contract with the experiment: the manual branch stays whole, because the
 * AI is an addition to it and the flag can be turned off mid-week.
 *
 * The whole state machine lives here rather than in `onboarding-modal.tsx` so
 * the modal keeps its existing shape and its existing tests keep passing with
 * the flag off.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocaleNavigate } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { countryConfig } from '../country';
import { AI_MAX_TEXT, reportOutcomeQuietly } from '../lib/ai-search-client';
import { useAiPropose } from '../lib/use-ai-propose';
import {
  COMPANY_ID_ALPHANUMERIC,
  COMPANY_ID_INPUT_MODE,
  cleanCompanyIdInput,
  isCompleteCompanyId,
} from '../lib/company-id-input';
import { countEdits, draftFromProposal, type AiDraft } from '../lib/ai-proposal-draft';
import { saveAiDraft } from '../lib/ai-save-search';
import type { NicheFirstMatch, SavedSearch } from '../lib/saved-search-client';
import { ApiError, NetworkError } from '../lib/api';
import { useAuthOptional } from '../lib/auth-context';
import { savedSearchCapFor } from '../lib/saved-search-cap';
import {
  AiErrorCard,
  AiNotUnderstoodCard,
  AiQuotaLine,
  AiQuotaOutCard,
  AiSparkle,
  aiPrimaryClass,
} from './ai-search-cards';
import { AiProposalReview } from './ai-proposal-review';
import { SavedSearchEditSheet } from './saved-search-edit-sheet';

const TIN_DIGITS = countryConfig.companyIdForm.digits;

/**
 * The «o NIF/БИН» door under the description. Off on Spain
 * (`features.aiCompanyStep`, owner 2026-10-22): step 1 is the description
 * alone, and the company id is asked in the company step, which offers the
 * codes of its awards (`CompanyCodesOffer`) instead of turning them into a
 * proposal unasked. On Kazakhstan the door stays exactly as it was.
 */
function companyDoor(): boolean {
  return !countryConfig.features.aiCompanyStep;
}

/**
 * «Изменить» on the proposal goes back to the description (Spain,
 * `features.aiOnboardingReentry`, negative QA 22.10 C10): it used to SAVE the
 * search and open the filter editor, so a reader who pressed it to reword his
 * text was already subscribed, and the second free attempt the counter promised
 * («2 de 2») could not be reached from the sheet at all. Off: as before.
 */
function editGoesBack(): boolean {
  return countryConfig.features.aiOnboardingReentry === true;
}

/** A complete company id: exactly TIN_DIGITS digits (UZ, KZ — as before), or the
 *  NIF shape (ES). */
function tinComplete(tin: string): boolean {
  return COMPANY_ID_ALPHANUMERIC ? isCompleteCompanyId(tin) : tin.length === TIN_DIGITS;
}

/**
 * Enough to work with? Three significant words, or a complete company id.
 *
 * Below that the button is DISABLED rather than red: nothing is wrong yet, the
 * person is still typing. A validation error on a field someone has not
 * finished is how a form teaches people it dislikes them — and a refusal costs
 * nothing here, whereas sending «насос» to the model costs money and comes back
 * as «не понял», which reads like our failure rather than a short sentence.
 */
export function canAsk(text: string, tin: string): boolean {
  if (tinComplete(tin)) return true;
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.replace(/[^\p{L}\p{N}]/gu, '').length >= 2);
  return words.length >= 3;
}

/**
 * A Spanish company id as one word: a letter or digit, seven digits, a letter
 * or digit — with at least one LETTER, which every NIF, CIF and NIE has, so a
 * phone number or a CPV code with its check digit («90910000-9») is not one.
 */
const NIF_WORD = /^(?:[A-Z]\d{7}[A-Z0-9]|\d{8}[A-Z])$/i;
/** The word that names it. */
const NIF_NAME = /^(?:nif|cif|nie)$/i;

function bareWord(w: string): string {
  return w.replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Does the description hold a company id rather than (only) a description?
 * Spain's step 1 (negative QA 22.10, C4/C5): «B12345674», «NIF B12345674» and
 * «Mi NIF es B86479078» were a disabled button with no reason given, or a 422
 * «no he entendido» about a text the reader never meant as one. The whole text
 * shaped like a NIF, a text starting with «NIF/CIF/NIE», or a NIF next to the
 * word naming it.
 */
export function nifInDescription(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (NIF_WORD.test(t.replace(/[\s.-]/g, ''))) return true;
  const words = t.split(/\s+/).map(bareWord);
  if (NIF_NAME.test(words[0] ?? '')) return true;
  return words.some((w) => NIF_WORD.test(w)) && words.some((w) => NIF_NAME.test(w));
}

/** The id itself, upper-cased, for the company step's field — or null. */
export function nifFromDescription(text: string): string | null {
  const whole = text.trim().replace(/[\s.-]/g, '');
  if (NIF_WORD.test(whole)) return whole.toUpperCase();
  const word = text
    .split(/\s+/)
    .map(bareWord)
    .find((w) => NIF_WORD.test(w));
  return word ? word.toUpperCase() : null;
}

/** The description as the model would read it without the id and its name. */
function withoutNif(text: string): string {
  return text
    .split(/\s+/)
    .filter((w) => {
      const bare = bareWord(w);
      return !NIF_WORD.test(bare) && !NIF_NAME.test(bare);
    })
    .join(' ');
}

export function AiOnboardingStep({
  titleId,
  onManual,
  onSaved,
  onCompanyStep,
}: {
  titleId: string;
  /** Hand over to the existing category picker (мокап 5: every card offers it). */
  onManual: (reason: 'link' | 'not_understood' | 'quota' | 'error') => void;
  /** A search now exists — the wizard moves on exactly as it does after a
   *  manual pick (invalidate, sample alert, next step). */
  onSaved: (search: SavedSearch, firstMatch: NicheFirstMatch | null) => void;
  /**
   * Spain: go to the company step now — the button under «El NIF se pide en el
   * paso siguiente» — with the NIF found in the text, if any, to put in its
   * field. Absent when this visit has no company step to go to.
   */
  onCompanyStep?: (nif: string | null) => void;
}) {
  const { t, locale } = useLocale();
  const navigate = useLocaleNavigate();
  // Optional: the step is also rendered (and unit-tested) outside AuthProvider.
  const user = useAuthOptional()?.user ?? null;
  const [text, setText] = useState('');
  const [tin, setTin] = useState('');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The row to open the standard builder on, after «Изменить» saved it. */
  const [editTarget, setEditTarget] = useState<SavedSearch | null>(null);
  const [pendingFirstMatch, setPendingFirstMatch] = useState<NicheFirstMatch | null>(null);

  const ai = useAiPropose('onboarding', locale, true);
  const textRef = useRef<HTMLTextAreaElement>(null);

  /**
   * The company id alone found nothing to build on (`ai_tin_unknown`, Kazakhstan
   * only). That is a gap in OUR data, not something the person got wrong, so it
   * is not a failure card: step 1 stays, says so, and asks the question.
   */
  const tinUnknown = ai.phase === 'failed' && ai.failure === 'ai_tin_unknown';
  useEffect(() => {
    if (!tinUnknown) return;
    // The id has already told us everything it can — the next ask is the
    // description alone, and the button waits for it rather than re-sending
    // the same id for the same answer.
    setTin('');
    textRef.current?.focus();
  }, [tinUnknown]);

  function ask() {
    setDraft(null);
    setSaveError(null);
    ai.ask(tinComplete(tin) ? { tin, text: text.trim() || undefined } : { text });
  }

  // Seed the editable draft the first time a proposal lands (and again for
  // each new one). Done on render rather than in an effect so the review never
  // paints one frame with no chips.
  const proposal = ai.phase === 'ready' ? ai.result : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (proposal && seededFor !== proposal.draftId) {
    setSeededFor(proposal.draftId);
    setDraft(draftFromProposal(proposal.proposal));
  }

  /**
   * What became of the draft, kept in a ref so the unmount reporter below can
   * read it without re-subscribing on every keystroke.
   */
  const fate = useRef<{ draftId: string | null; settled: boolean; edits: number }>({
    draftId: null,
    settled: false,
    edits: 0,
  });
  const fateDraftId = proposal?.draftId ?? fate.current.draftId;
  // A NEW draft starts its own record. Only reachable through «Cambiar» → back
  // (Spain); the first proposal of a mount starts from the initial record anyway.
  if (fateDraftId !== fate.current.draftId) {
    fate.current = { draftId: fateDraftId, settled: false, edits: 0 };
  }
  if (proposal && draft) fate.current.edits = countEdits(proposal.proposal, draft);

  /**
   * A proposal the user walked away from is an OUTCOME, not an absence.
   *
   * Without this the drafts table would show «proposed» forever for everyone
   * who closed the sheet, and «доля принятых без правок» — the number this
   * whole experiment is judged on — would divide by a denominator that quietly
   * mixes "still deciding" with "did not want it".
   */
  useEffect(() => {
    return () => {
      if (!fate.current.draftId || fate.current.settled) return;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'abandoned',
        editsCount: fate.current.edits,
      });
    };
  }, []);

  /** Leaving for the manual picker closes the draft out honestly. */
  function leaveToManual(reason: 'link' | 'not_understood' | 'quota' | 'error') {
    if (fate.current.draftId && !fate.current.settled) {
      fate.current.settled = true;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'manual',
        editsCount: fate.current.edits,
      });
    }
    onManual(reason);
  }

  /**
   * «Cambiar» on Spain (`editGoesBack`): back to step 1 with the text as it was
   * and nothing saved. The proposal left behind is closed out as `abandoned` —
   * the reader did not take it as it was — and the counter shows what the
   * answer left: the proposal was made, so its attempt is spent, and the next
   * «Buscar» is the second one.
   */
  function backToDescription() {
    if (fate.current.draftId && !fate.current.settled) {
      fate.current.settled = true;
      reportOutcomeQuietly(fate.current.draftId, {
        outcome: 'abandoned',
        editsCount: fate.current.edits,
      });
    }
    setDraft(null);
    setSeededFor(null);
    setSaveError(null);
    ai.reset();
    requestAnimationFrame(() => textRef.current?.focus());
  }

  async function save(openBuilder: boolean) {
    if (!proposal || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      // `replace: true` marks the row `fromOnboarding` server-side, as the
      // manual picker's saves are. Unlike the manual niche-switch it does NOT
      // license retiring anything: with `aiDraftId` present the server refuses
      // at the free cap (`saved_search_limit`) instead of switching off the
      // reader's oldest search — see notifications.controller.
      const res = await saveAiDraft(draft, { draftId: proposal.draftId, replace: true });
      // The create endpoint closed the draft out itself (saved / edited_saved),
      // so the unmount reporter must not follow it with «abandoned».
      fate.current.settled = true;
      if (openBuilder) {
        // «Изменить»: the search exists, so the STANDARD builder can open on it
        // — prefilled with exactly this proposal, with every axis the sheet
        // supports. Onboarding advances when the sheet closes, not before.
        setPendingFirstMatch(res.firstMatch);
        setEditTarget(res.search);
      } else {
        onSaved(res.search, res.firstMatch);
      }
    } catch (e: unknown) {
      // A server refusal carries a message worth showing (over the cap,
      // unauthorised); a dropped mobile connection carries «Failed to fetch»,
      // which is English, technical and about nothing the user did.
      // At the free cap the server refuses an ИИ-подбор outright — it never
      // retires a search the reader built by hand (notifications.controller).
      // Say so in the reader's language with the cap the API reported, rather
      // than its English `message`.
      const capFromApi =
        e instanceof ApiError && typeof e.details?.['limit'] === 'number'
          ? (e.details['limit'] as number)
          : null;
      const cap = capFromApi ?? savedSearchCapFor(user);
      setSaveError(
        e instanceof ApiError && e.code === 'saved_search_limit'
          ? t('saved.limitReached', { n: Number.isFinite(cap) ? cap : 1 })
          : e instanceof NetworkError
            ? t('onboarding.niche.saveNetworkError')
            : e instanceof Error && e.message
              ? e.message
              : t('aiOnboarding.saveError'),
      );
    } finally {
      setSaving(false);
    }
  }

  // ── The proposal ────────────────────────────────────────────────────────
  if (proposal && draft) {
    return (
      <>
        <h2
          id={titleId}
          className="mb-2 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
        >
          {t('aiOnboarding.step2.title')}
        </h2>
        <AiProposalReview
          response={proposal}
          draft={draft}
          onDraftChange={setDraft}
          onSave={() => void save(false)}
          onEdit={editGoesBack() ? backToDescription : () => void save(true)}
          editIsBack={editGoesBack()}
          saving={saving}
          saveError={saveError}
        />
        {/* The standard builder, opened on the row «Изменить» just created. It
            portals above this modal (both are z-50; the later portal wins), so
            the wizard is still underneath when the sheet closes. */}
        <SavedSearchEditSheet
          search={editTarget}
          open={editTarget !== null}
          // No second «Заполнить с ИИ» here — see the prop's own note.
          aiFill={false}
          onOpenChange={(open) => {
            if (open || !editTarget) return;
            const saved = editTarget;
            setEditTarget(null);
            onSaved(saved, pendingFirstMatch);
          }}
        />
      </>
    );
  }

  // ── The five "no proposal" screens (макет 5) ────────────────────────────
  if (ai.phase === 'failed' && !tinUnknown) {
    if (ai.failure === 'ai_not_understood') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiNotUnderstoodCard
            // «Уточнить» keeps whatever was typed — re-typing a sentence we
            // just rejected is where people give up.
            onRefine={ai.reset}
            onManual={() => leaveToManual('not_understood')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    if (ai.failure === 'ai_quota_exhausted') {
      return (
        <>
          <h2 id={titleId} className="sr-only">
            {t('aiOnboarding.step1.title')}
          </h2>
          <AiQuotaOutCard
            quota={ai.quota}
            onManual={() => leaveToManual('quota')}
            onPro={() => navigate('/plans?src=ai_onboarding')}
          />
          <p className="mt-3 text-center text-[12px] text-ink-muted">
            {t('aiOnboarding.manualFooter')}
          </p>
        </>
      );
    }
    // timeout · 502 · network · anything unrecognised. None of them spends an
    // attempt, and the copy says so — see `AiErrorCard`.
    return (
      <>
        <h2 id={titleId} className="sr-only">
          {t('aiOnboarding.step1.title')}
        </h2>
        <AiErrorCard onManual={() => leaveToManual('error')} onRetry={ai.retry} />
        <p className="mt-3 text-center text-[12px] text-ink-muted">
          {t('aiOnboarding.manualFooter')}
        </p>
      </>
    );
  }

  // ── Шаг 1 ───────────────────────────────────────────────────────────────
  const busy = ai.phase === 'loading';
  // Spain (no company-id door here): a NIF in the description belongs to the
  // next step — say so before any request, and judge the rest of the text alone
  // («Mi NIF es B86479078» is no description). KZ/UZ: as before.
  const nifAsked = !companyDoor() && nifInDescription(text);
  const ready = nifAsked ? canAsk(withoutNif(text), '') : canAsk(text, tin);
  // Spain: a disabled «Buscar» says why (QA 22.10, C1 — «limpieza» alone was a
  // pale button and nothing else).
  const whyDisabled = !companyDoor() && !ready && !busy && !nifAsked;
  return (
    <>
      <h2
        id={titleId}
        className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
      >
        {t('aiOnboarding.step1.title')}
      </h2>
      {tinUnknown ? (
        <p
          role="status"
          className="mb-3 rounded-lg border border-brand/25 bg-brand-dim px-3 py-2 text-[13px] leading-[1.55] text-ink"
        >
          {t('aiOnboarding.tinUnknown.desc')}
        </p>
      ) : (
        <p className="mb-3 text-[13px] leading-[1.55] text-ink-soft">
          {t('aiOnboarding.step1.desc')}
        </p>
      )}

      <textarea
        ref={textRef}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, AI_MAX_TEXT))}
        rows={3}
        maxLength={AI_MAX_TEXT}
        disabled={busy}
        aria-label={t('aiOnboarding.step1.title')}
        placeholder={t('aiOnboarding.step1.placeholder')}
        className="w-full resize-none rounded-lg border border-edge bg-bone px-3 py-2.5 text-[16px] leading-snug text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
      />
      <div className="mt-1.5 flex items-start justify-between gap-3">
        <p className="text-[12px] leading-snug text-ink-muted">{t('aiOnboarding.step1.example')}</p>
        {/* BEFORE the button, never after: someone who spends their last
            attempt on half a sentence was never told it was the last one. */}
        <AiQuotaLine quota={ai.quota} variant="counter" className="shrink-0" />
      </div>

      {companyDoor() && (
        <>
          <div className="my-3 flex items-center gap-3">
            <span className="h-px flex-1 bg-edge" />
            <span className="text-[12px] text-ink-muted">{t('aiOnboarding.step1.or')}</span>
            <span className="h-px flex-1 bg-edge" />
          </div>

          {/* The ИНН door. Cheapest and most verifiable of the three inputs: the
              codes come from the company's OWN past wins, so the model is only
              naming and wording them. */}
          <label
            className="mb-1 block text-[13px] font-semibold text-ink"
            htmlFor="ai-onboarding-tin"
          >
            {t('aiOnboarding.step1.tinLabel')}
          </label>
          <input
            id="ai-onboarding-tin"
            type="text"
            inputMode={COMPANY_ID_INPUT_MODE}
            maxLength={COMPANY_ID_ALPHANUMERIC ? undefined : TIN_DIGITS}
            value={tin}
            disabled={busy}
            onChange={(e) =>
              setTin(
                COMPANY_ID_ALPHANUMERIC
                  ? cleanCompanyIdInput(e.target.value)
                  : e.target.value.replace(/\D/g, ''),
              )
            }
            placeholder={t('company.tinPlaceholder', { digits: TIN_DIGITS })}
            className="h-11 w-full rounded-md border-[1.5px] border-edge bg-bone px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
          />
          <p className="mt-1 text-[12px] text-ink-muted">{t('aiOnboarding.step1.tinHint')}</p>
        </>
      )}

      {nifAsked && (
        <div
          role="status"
          className="mt-3 rounded-lg border border-brand/25 bg-brand-dim px-3 py-2 text-[13px] leading-[1.55] text-ink"
        >
          {onCompanyStep ? t('aiOnboarding.step1.nifHint') : t('aiOnboarding.step1.nifNoStep')}
          {onCompanyStep && (
            <button
              type="button"
              onClick={() => onCompanyStep(nifFromDescription(text))}
              className="mt-1 block font-semibold text-brand underline underline-offset-2 transition-colors hover:text-brand-mid"
            >
              {t('aiOnboarding.step1.nifGo')}
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={ask}
        disabled={!ready || busy}
        aria-describedby={whyDisabled ? 'ai-step1-why' : undefined}
        className={`${aiPrimaryClass} mt-4 h-12 w-full`}
      >
        {/* One keyed element per state, not a text node that changes: under
            Chrome's translator the old text sits in a <font> React no longer
            reaches, so «Buscar» stayed on screen while busy (Spain, 22.10). */}
        {busy ? (
          <span key="busy">{t('aiOnboarding.step1.loading')}</span>
        ) : (
          <span key="idle" className="inline-flex items-center">
            <AiSparkle className="mr-1.5 text-white" />
            {t('aiOnboarding.step1.submit')}
          </span>
        )}
      </button>
      {whyDisabled && (
        <p id="ai-step1-why" className="mt-1.5 text-center text-[12.5px] text-ink-muted">
          {t('aiOnboarding.step1.needWords')}
        </p>
      )}

      {/* Sonnet answers in 6–10 s, so a spinner alone would read as a hang.
          Skeleton rows say "something is being built" and occupy the space the
          chips will take, which stops the sheet jumping when they land. */}
      {busy && (
        <div className="mt-3 flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-7 w-full animate-pulse rounded-full bg-bone" />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => leaveToManual('link')}
        className="mt-3 w-full text-center text-[13px] font-semibold text-ink-soft underline underline-offset-2 transition-colors hover:text-ink"
      >
        {t('aiOnboarding.step1.manual')}
      </button>
    </>
  );
}
